import React from "react";
import { ArrowDownRight, Church, Edit2, X } from "lucide-react";

export default function DashboardBannerSection({
  bannerRef,
  longPressHandlers,
  isMobile,
  isEditMode,
  currentSkyTheme,
  isDraggingMessage,
  bannerMessage,
  handleMessageMouseDown,
  setMessageForm,
  displayMessage,
  setIsMessageModalOpen,
  dateTimeInfo,
  churchName,
  deptName,
  userName,
  greetingInfo,
  deleteWidget,
}) {
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div
        ref={bannerRef}
        {...longPressHandlers}
        className={`relative rounded-[1.75rem] overflow-hidden ${isMobile ? "" : "shadow-2xl"} flex flex-col justify-between border border-white/20 group w-full ${isEditMode ? "animate-shake ring-4 ring-blue-400/50 z-40" : ""}`}
        style={{
          height: isMobile ? "100%" : "100%",
          backgroundImage: currentSkyTheme.gradient,
          backgroundSize: "cover",
          backgroundPosition: "bottom center",
          backgroundRepeat: "no-repeat",
          animation: currentSkyTheme.celestial.type === "moon" ? "none" : "skyGradient 20s ease infinite",
          animationDelay: isEditMode ? `${Math.random() * -0.5}s` : "0s",
        }}
      >
        <style>{`
          @keyframes skyGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes moveX {
            0% { left: 110%; }
            100% { left: -10%; }
          }
          @keyframes moveY {
            0% { top: 100%; }
            50% { top: 10%; }
            100% { top: 100%; }
          }
          @keyframes floatCloud {
            0% { transform: translateX(0px); }
            100% { transform: translateX(0px); }
          }
          .cloud {
            background: #fff;
            border-radius: 100px;
            position: absolute;
          }
          .cloud:after, .cloud:before {
            background: #fff;
            content: '';
            position: absolute;
            z-index: -1;
          }
          .cloud:after {
            border-radius: 100px;
            height: 80%;
            left: 15%;
            top: -40%;
            width: 30%;
          }
          @keyframes shake {
            0% { transform: translate(0px, 0px) rotate(0deg); }
            10% { transform: translate(-0.3px, -0.3px) rotate(-0.3deg); }
            20% { transform: translate(-0.3px, 0px) rotate(0.3deg); }
            30% { transform: translate(0.3px, 0.3px) rotate(0deg); }
            40% { transform: translate(0.3px, -0.3px) rotate(0.3deg); }
            50% { transform: translate(-0.3px, 0.3px) rotate(-0.3deg); }
            60% { transform: translate(-0.3px, 0.3px) rotate(0deg); }
            70% { transform: translate(0.3px, 0.3px) rotate(-0.3deg); }
            80% { transform: translate(-0.3px, -0.3px) rotate(0.3deg); }
            90% { transform: translate(0.3px, 0.3px) rotate(0deg); }
            100% { transform: translate(0px, 0px) rotate(-0.3deg); }
          }
          .animate-shake {
            animation: shake 1.5s linear infinite;
          }
          .cloud:before {
            border-radius: 200px;
            height: 150%;
            right: 15%;
            top: -70%;
            width: 50%;
          }
          @keyframes moveClouds {
            0% { left: -20%; opacity: 0; }
            10% { opacity: 0.8; }
            90% { opacity: 0.8; }
            100% { left: 120%; opacity: 0; }
          }
          @keyframes pulse-subtle {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
          .animate-pulse-subtle {
            animation: pulse-subtle 2s ease-in-out infinite;
          }
        `}</style>
        {!isMobile && (
          <div
            className={`absolute ${isDraggingMessage ? "" : "transition-all duration-200"} ${isEditMode ? "cursor-move hover:scale-105 z-50" : ""}`}
            style={{
              left: bannerMessage.isRightAligned ? "auto" : `${bannerMessage.x}%`,
              right: bannerMessage.isRightAligned ? "1.25rem" : "auto",
              top: bannerMessage.isRightAligned ? "auto" : `${bannerMessage.y}%`,
              bottom: bannerMessage.isRightAligned ? "1.25rem" : "auto",
              zIndex: 20,
            }}
            onMouseDown={handleMessageMouseDown}
            onDoubleClick={(e) => {
              if (isEditMode) {
                e.stopPropagation();
                setMessageForm({ text: displayMessage.text, source: displayMessage.ref || "" });
                setIsMessageModalOpen(true);
              }
            }}
          >
            <div className={`bg-black/40 backdrop-blur-xl px-6 py-3 rounded-3xl border-l-4 border-yellow-400/80 text-left inline-block shadow-lg max-w-[300px] md:max-w-md relative group/msg ${isEditMode ? "ring-2 ring-yellow-400 ring-offset-2 ring-offset-transparent animate-pulse-slow" : ""}`}>
              <p className="text-white text-lg md:text-xl font-medium font-serif-kr leading-relaxed drop-shadow-md whitespace-pre-wrap select-none">"{displayMessage.text}"</p>
              {displayMessage.ref && <p className="text-sm text-white/70 font-bold tracking-wider mt-2 select-none">- {displayMessage.ref}</p>}
              {isEditMode && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMessageForm({ text: displayMessage.text, source: displayMessage.ref || "" });
                    setIsMessageModalOpen(true);
                  }}
                  className="absolute -top-3 -right-3 bg-white text-[#354F38] p-2 rounded-full shadow-lg hover:scale-110 transition z-50"
                >
                  <Edit2 size={14} />
                </button>
              )}
            </div>
          </div>
        )}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          <div className="absolute w-24 h-full" style={{ animation: "moveX 60s linear infinite" }}>
            <div className="absolute w-24 h-24 rounded-full" style={{ backgroundColor: currentSkyTheme.celestial.color, boxShadow: currentSkyTheme.celestial.shadow, animation: "moveY 60s ease-in-out infinite" }}>
              {currentSkyTheme.celestial.type === "moon" && <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] rounded-full"></div>}
            </div>
          </div>
        </div>
        {currentSkyTheme.celestial.type !== "moon" && (
          <>
            <div className="cloud w-48 h-16 opacity-80 blur-md" style={{ top: "20%", animation: "moveClouds 45s linear infinite", animationDelay: "0s" }}></div>
            <div className="cloud w-32 h-12 opacity-60 blur-sm" style={{ top: "40%", animation: "moveClouds 35s linear infinite", animationDelay: "-15s" }}></div>
            <div className="cloud w-64 h-24 opacity-40 blur-xl" style={{ top: "10%", animation: "moveClouds 60s linear infinite", animationDelay: "-5s" }}></div>
          </>
        )}
        <div className="relative z-10 flex justify-end items-start pt-5 pr-5 pl-6 md:pl-8">
          {!isMobile && (
            <div className="flex items-center gap-4 bg-black/30 backdrop-blur-md border border-white/10 px-6 py-3 rounded-[2rem] shadow-lg">
              <div className="flex flex-col items-end text-right">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold text-white/80 drop-shadow-sm">{dateTimeInfo.ampm}</span>
                  <span className="text-2xl md:text-3xl font-black text-white drop-shadow-md font-do-hyeon tracking-wide leading-none">{dateTimeInfo.time}</span>
                </div>
                <span className="text-xs md:text-sm font-bold text-white/80 drop-shadow-sm tracking-wider">{dateTimeInfo.date}</span>
              </div>
              <div className="w-[1px] h-10 bg-white/20"></div>
              <div className="flex flex-col items-center justify-center">
                {dateTimeInfo.weatherIcon}
                <span className="text-[10px] font-bold text-white/90 mt-1">{dateTimeInfo.weatherText}</span>
              </div>
            </div>
          )}
        </div>
        <div className={`relative z-10 mt-auto pl-6 md:pl-8 pr-5 md:pr-5 ${isMobile ? "pb-4" : "pb-6 md:pb-8"} pt-0 h-full flex flex-col justify-end`}>
          <div className="flex items-center gap-2 text-yellow-200 font-bold mb-3 uppercase text-xs tracking-widest bg-black/30 w-fit px-4 py-1.5 rounded-full backdrop-blur-md border border-white/10 shadow-sm">
            <Church size={12} /> {churchName} {deptName}
          </div>
          <div className="flex items-end gap-4 relative">
            <div>
              <h1 className={`${isMobile ? "text-2xl" : "text-3xl md:text-5xl"} font-black mb-2 font-do-hyeon leading-tight drop-shadow-lg tracking-tight text-white`}>
                반갑습니다, <span className="text-yellow-300 relative inline-block">{userName}<svg className="absolute w-full h-3 bottom-1 left-0 -z-10 opacity-40" viewBox="0 0 100 10" preserveAspectRatio="none"><path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" /></svg></span>님!
              </h1>
              <div className="flex items-center gap-3">
                <p className={`${isMobile ? "text-base" : "text-lg md:text-xl"} font-bold text-white/90 drop-shadow-sm`}>{greetingInfo.text}</p>
                <img src={greetingInfo.icon} width={40} alt="weather icon" className="shadow-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isEditMode && !isMobile && (
        <>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              deleteWidget("banner");
            }}
            className="non-draggable absolute -top-2 -right-2 bg-red-500 text-white p-1.5 rounded-full shadow-lg hover:scale-110 transition z-50 cursor-pointer"
          >
            <X size={16} />
          </button>
          <div
            className="react-resizable-handle react-resizable-handle-se absolute bottom-2 right-2 w-8 h-8 bg-white/30 backdrop-blur-md border border-white/60 text-[#2C3E30] rounded-full shadow-lg flex items-center justify-center cursor-se-resize z-50 hover:scale-110 hover:bg-white/50 transition-all"
            style={{ touchAction: "none" }}
            onClick={(e) => e.stopPropagation()}
          >
            <ArrowDownRight size={18} strokeWidth={3} />
          </div>
        </>
      )}
    </div>
  );
}
